import { assign, createMachine } from 'xstate';

import { getURLSearchParam, INVITE_CODE } from '@nw/shared/utils';

import {
  FORMS_MACHINE_ID,
  productFormMachineActions,
  productFormsMachineEvents,
  productFormsMachineStates,
  productFormsMachineTags
} from '../../constants/formMachineConstants';

import { ProductFormsMachineContext, ProductFormsMachineEvent } from './types';

const tags = productFormsMachineTags;
const actions = productFormMachineActions;
const states = productFormsMachineStates;
const events = productFormsMachineEvents;

//TODO: After finalizing segment flow , setup values in context
export const publicFormsMachine = (
  isAlreadyLoggedInUser: boolean,
  shouldLoggedOutUser: boolean
) =>
  createMachine<ProductFormsMachineContext, ProductFormsMachineEvent>({
    id: FORMS_MACHINE_ID,
    initial:
      shouldLoggedOutUser || !isAlreadyLoggedInUser
        ? states.loggingOutUserState
        : states.getProfileDetailsState,
    context: {
      productInfo: null,
      shouldShowGetOTP: false,
      next: () => { },
      back: () => { },
      eventsFromConfig: [],
      goToNextSteps: [],
      isOtpSentToUser: false,
      isLoggedIn: isAlreadyLoggedInUser ?? false,
      values: {},
      inviteCode: getURLSearchParam().has(INVITE_CODE)
        ? (getURLSearchParam().get(INVITE_CODE) as string)
        : '',
      userProfileDetails: null,
      orderDetails: null,
      isNewUser: false,
      authCode: '',
      referralCodes: {},
      isInviteCodePresentInBackend: false,
      errorMessage: '',
      isOtpVerified: false,
      isNextAndSubmitBtnLoading: false,
      formSubmittedValues: {},
      authMachineRef: null,
      isOTPButtonLoading: false,
      formattedRequestObjectKeys: null,
      updateProfileRequestObject: null
    },
    on: {
      [events.goToPreviousStep]: {
        actions: ['callBackFromContext']
      },

      [events.goToNextStep]: {
        actions: ['callNextFromContext']
      },

      [events.clearErrorMessage]: {
        actions: ['clearErrorMessageFromContext', 'callEventsFromConfig']
      },

      [events.goToNotLoggedInUserState]: {
        target: states.notLoggedInUserState
      },

      [events.assignConfigEventsToContext]: {
        actions: 'assignConfigEventsToContext'
      },

      [events.goToUpdateProfileDetailsState]: {
        actions: ['updateProfileRequestObject'],
        target: states.updateProfileState
      },

      [events.goToCreateFreeOrderState]: {
        target: states.createFreeOrderState
      },

      [events.callEventsFromConfig]: {
        actions: 'callEventsFromConfig'
      },

      [events.goToUpdateUserExtraInfoState]: {
        target: states.updateUserExtraInfoState
      },
      [events.loggedOutUser]: {
        target: states.loggingOutUserState
      },
      [events.clearPrefilledFormData]: {
        actions: [actions.clearPrefilledFormDataAction]
      },
      [events.onSuccessOtpVerification]: {
        actions: [actions.setLoginAccessAndRefreshToken],
        target: states.getProfileDetailsState
      },
      [events.setGoToNextSteps]: {
        actions: ['setGoToNextSteps', 'callEventsFromConfig']
      },
      [events.onEntryGetOTP]: {
        actions: [
          actions.setIsOtpButtonLoadingToTrue,
          assign((context: ProductFormsMachineContext) => {
            if (!context.shouldShowGetOTP)
              return {
                isNextAndSubmitBtnLoading: true
              };
            else return {};
          }),
          actions.clearErrorMessageFromContext,
          actions.resetOtp
        ]
      },
      [events.onSuccessGetOTP]: {
        actions: [
          actions.setIsOtpButtonLoadingToFalse,
          actions.setNextAndSubmitBtnLoadingToFalse,
          actions.updateIsOtpSentToUserInContext,
          actions.sendGetOtpCustomEventToDataLayer,
          actions.updateIsNewUserInContextToFalse,
          actions.callEventsFromConfig
        ]
      },
      [events.onErrorGetOTP]: {
        actions: [
          actions.setNextAndSubmitBtnLoadingToFalse,
          actions.assignChildSelfTransitionToConfigEvents,
          actions.assignErrorMessageToContext
        ]
      },
      [events.doChildMachineSelfTransition]: {
        actions: [actions.doChildMachineSelfTransition]
      },
      [events.onSuccessGetSignUpOTP]: {
        actions: [
          actions.setIsOtpButtonLoadingToFalse,
          actions.setNextAndSubmitBtnLoadingToFalse,
          actions.sendGetOtpCustomEventToDataLayer,
          actions.clearErrorMessageFromContext,
          actions.updateIsOtpSentToUserInContext,
          actions.updateIsNewUserInContextToTrue,
          actions.callEventsFromConfig
        ]
      },
      [events.resendOtpClicked]: {
        actions: [actions.sendResendOtpEventToDataLayer]
      },
      [events.sendGAEvent]: {
        actions: [actions.sendGAEventToDataLayer, actions.callEventsFromConfig]
      },
      [events.sendFrontendSegmentEvent]: {
        actions: [
          actions.sendFrontendSegmentEvent,
          actions.callEventsFromConfig
        ]
      },
      [events.sendBackendSegmentEvent]: {
        target: states.sendBackendTrackEventSegmentApiState
      },
      [events.resetAuthMachine]: {
        actions: [
          assign((context: ProductFormsMachineContext) => {
            return {
              authMachineRef: null,
              isOtpSentToUser: false
            };
          })
        ]
      },
      [events.goToOtpVerificationStep]: [
        {
          cond: 'isNotLoggedIn',
          target: states.initializeAuthMachineState
        },
        {
          actions: ['callEventsFromConfig']
        }
      ]
    },
    states: {
      [states.loggingOutUserState]: {
        tags: [tags.formLoading],
        entry: ['logoutFromSegment'],
        invoke: {
          src: 'logoutUser',
          onDone: [
            {
              cond: 'isProductInfoPresent',
              actions: ['clearErrorMessageFromContext', 'callEventsFromConfig']
            },
            {
              cond: 'shouldLoadScriptLocally',
              actions: 'assignProductConfigLocally',
              target: states.notLoggedInUserState
            },
            {
              target: states.getInitialConfigState
            }
          ],
          onError: [
            {
              cond: 'isProductInfoPresent',
              actions: ['clearErrorMessageFromContext', 'callEventsFromConfig']
            },
            {
              target: states.getInitialConfigState
            }
          ]
        }
      },

      [states.getInitialConfigState]: {
        tags: [tags.getInitialConfigLoadingTag],
        invoke: {
          src: 'getInitialConfig',
          onDone: [
            {
              target: states.notLoggedInUserState,
              actions: ['validateDataAndAssignProductConfigToContext']
            }
          ],
          onError: {
            target: states.getInitialConfigFailureState
          }
        }
      },
      [states.getInitialConfigFailureState]: {
        on: {
          [events.getInitialConfigRetryEvent]: states.getInitialConfigState
        }
      },
      [states.notLoggedInUserState]: {
        entry: ['setNextAndSubmitBtnLoadingToFalse', 'callEventsFromConfig'],
        on: {
          [events.goToOtpVerificationStep]: [
            {
              cond: 'isNotLoggedIn',
              target: states.initializeAuthMachineState
            },
            {
              actions: ['callEventsFromConfig']
            }
          ]
        }
      },
      [states.initializeAuthMachineState]: {
        entry: [actions.spawnAuthCoreMachine]
      },

      [states.getProfileDetailsState]: {
        tags: [tags.otpVerifyBtnLoading],
        entry: [
          'setNextAndSubmitBtnLoadingToTrue',
          'setIsOtpVerifiedInContext'
        ],
        invoke: {
          src: 'getProfileDetails',
          onDone: [
            {
              cond: () => !isAlreadyLoggedInUser || shouldLoggedOutUser,
              actions: [
                'validateAndSetUserProfileDetailsToContext',
                'saveUserIdToCookie',
                'loadSegmentScript',
                'sendIdentifySegmentEvent',
                'sendOTPVerifiedOTPAnalytics',
                'clearErrorMessageFromContext'
              ],
              target: states.getClientAuthCodeState
            },
            {
              cond: () => isAlreadyLoggedInUser && !shouldLoggedOutUser,
              actions: [
                'validateAndSetUserProfileDetailsToContext',
                'saveUserIdToCookie',
                'loadSegmentScript',
                'sendIdentifySegmentEvent',
                'sendOTPVerifiedOTPAnalytics',
                'clearErrorMessageFromContext'
              ],
              target: states.getReferralCodesState
            }
          ],
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [events.doSelfTransition]: states.getProfileDetailsState
        }
      },
      [states.getClientAuthCodeState]: {
        tags: [tags.otpVerifyBtnLoading],
        entry: [
          'setNextAndSubmitBtnLoadingToTrue',
          'resetOTP',
          'sendUserVerifiedOtpEvent'
        ],
        invoke: {
          src: 'getClientAuthCode',
          onDone: {
            actions: 'onSuccessGettingClientAuthCode',
            target: states.getClientAuthenticationDetailsState
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [events.doSelfTransition]: states.getClientAuthCodeState
        }
      },
      [states.getClientAuthenticationDetailsState]: {
        tags: [tags.otpVerifyBtnLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'getClientAuthenticationDetails',
          onDone: {
            actions: [
              'onSuccessGettingClientAuthenticationDetails',
              'updateIsLoggedInInContext'
            ],
            target: states.getReferralCodesState
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [events.doSelfTransition]: states.getClientAuthenticationDetailsState
        }
      },

      [states.updateProfileState]: {
        tags: [tags.buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'updateProfile',
          onDone: {
            actions: [
              'clearErrorMessageFromContext',
              'sendIdentifySegmentEventOnOrderSuccess',
              'setNextAndSubmitBtnLoadingToFalse',
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [events.doSelfTransition]: states.updateProfileState
        }
      },
      [states.getReferralCodesState]: {
        tags: [tags.otpVerifyBtnLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'getReferralCodes',
          onDone: [
            {
              cond: 'shouldTrackSecondaryReferralAttempt',
              actions: [
                'setNextAndSubmitBtnLoadingToFalse',
                'assignReferralCodesToContext',
                'updateInviteCodeInContext',
                'updateIsInviteCodePresentInBackend',
                'clearErrorMessageFromContext',
                'callEventsFromConfig'
              ],
              target: states.trackReferralAttemptState
            },
            {
              cond: () => !isAlreadyLoggedInUser || shouldLoggedOutUser,
              actions: [
                'setNextAndSubmitBtnLoadingToFalse',
                'assignReferralCodesToContext',
                'updateInviteCodeInContext',
                'updateIsInviteCodePresentInBackend',
                'clearErrorMessageFromContext',
                'callEventsFromConfig'
              ]
            },
            {
              cond: () => isAlreadyLoggedInUser && !shouldLoggedOutUser,
              actions: ['updateInviteCodeInContext'],
              target: states.getInitialConfigState
            }
          ],
          onError: [
            {
              cond: 'isInvalidProductCodeErrorAndNotOtpVerified',
              actions: ['setNextAndSubmitBtnLoadingToFalse'],
              target: states.getInitialConfigState
            },
            {
              cond: 'isInvalidProductCodeErrorAndOtpVerified',
              actions: ['setNextAndSubmitBtnLoadingToFalse']
            },
            {
              actions: [
                'setNextAndSubmitBtnLoadingToFalse',
                'assignErrorMessageToContext',
                'assignSelfTransitionToConfigEvents'
              ]
            }
          ]
        },
        on: {
          [events.doSelfTransition]: states.getReferralCodesState
        }
      },
      [states.trackReferralAttemptState]: {
        tags: [tags.otpVerifyBtnLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'trackReferralAttempt',
          onDone: [
            {
              cond: () => !isAlreadyLoggedInUser || shouldLoggedOutUser,
              actions: ['setNextAndSubmitBtnLoadingToFalse', 'clearErrorMessageFromContext', 'callEventsFromConfig'],
            },
            {
              cond: () => isAlreadyLoggedInUser && !shouldLoggedOutUser,
              actions: ['setNextAndSubmitBtnLoadingToFalse', 'updateInviteCodeInContext'],
              target: states.getInitialConfigState
            }],
          onError: [
            {
              cond: 'isOtpNotVerified',
              actions: ['setNextAndSubmitBtnLoadingToFalse', 'onErrorTrackReferralAttemptLogEntry'],
              target: states.getInitialConfigState
            },
            {
              actions: ['setNextAndSubmitBtnLoadingToFalse', 'onErrorTrackReferralAttemptLogEntry']
            }]
        }
      },
      [states.createFreeOrderState]: {
        tags: [tags.buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'createFreeOrder',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'updateOrderDetailsInContext',
              'clearErrorMessageFromContext',
              'updateFormSubmittedValues'
            ],
            target: states.getOrderStatusState
          },
          onError: [
            {
              cond: 'isAlreadyRegisteredProductError',
              actions: [
                'setNextAndSubmitBtnLoadingToFalse',
                'assignErrorMessageToContext',
                'sendAlreadyRegisteredProductSegmentEvent',
                'assignSelfTransitionToConfigEvents'
              ]
            },
            {
              actions: [
                'setNextAndSubmitBtnLoadingToFalse',
                'assignErrorMessageToContext',
                'assignSelfTransitionToConfigEvents'
              ]
            }
          ]
        },
        on: {
          [events.doSelfTransition]: states.createFreeOrderState
        }
      },
      [states.getOrderStatusState]: {
        tags: [tags.buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'getOrderStatus',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'clearErrorMessageFromContext',
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [events.doSelfTransition]: states.getOrderStatusState
        }
      },
      [states.updateUserExtraInfoState]: {
        tags: [tags.submitBtnLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'updateUserExtraInfo',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'sendIdentifySegmentEventOnOrderSuccess',
              'updateFormSubmittedValues',
              'clearErrorMessageFromContext',
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [events.doSelfTransition]: states.updateUserExtraInfoState
        }
      },
      [states.sendBackendTrackEventSegmentApiState]: {
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'callBackendSegmentApi',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'clearErrorMessageFromContext',
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'captureSegmentApiFailureSentryException',
              'callEventsFromConfig'
            ]
          }
        }
      }
    }
  });
